import { canonicalIndustry } from "@sagot/constants";
import type { IconName } from "@sagot/ui";
import type { AgentSummary, IndustryTemplate, PlanUsage } from "../shared/types";

/** Public audience code (RAG spec §12: 1 = public … 4 = staff). */
export const PUBLIC_AUDIENCE = 1;
export const AUDIENCE_CODES = [1, 2, 3, 4] as const;
export const BLANK_TEMPLATE = "blank";
export const CREATE_HASH = "#new";
export const CREATE_QUERY = "new";

export const CHANNEL_ICONS: Readonly<Record<string, IconName>> = {
  widget: "globe",
  webapp: "app-window",
  mobile: "smartphone",
  messenger: "message-circle",
  email: "mail",
  api: "code",
  slack: "hash",
  teams: "users",
};

export interface AudienceSummary {
  label: string;
  isPublic: boolean;
}

/** Card line: "Public" when public is allowed, else "{lowest audience} only". */
export function agentAudience(codes: readonly number[], label: (code: number) => string, onlyLabel?: (text: string) => string): AudienceSummary {
  const sorted = [...codes].sort((a, b) => a - b);
  const lowest = sorted[0] ?? PUBLIC_AUDIENCE;
  if (lowest === PUBLIC_AUDIENCE) return { label: label(PUBLIC_AUDIENCE), isPublic: true };
  const text = label(lowest);
  return { label: onlyLabel ? onlyLabel(text) : text, isPublic: false };
}

/** Client-side search over name + description (case-insensitive). */
export function filterAgents(agents: readonly AgentSummary[], query: string): AgentSummary[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...agents];
  return agents.filter((a) => `${a.name} ${a.description ?? ""}`.toLowerCase().includes(needle));
}

export interface PlanState {
  used: number;
  limit: number | null;
  remaining: number | null;
  percent: number;
  full: boolean;
}

export function planState(plan: PlanUsage | undefined): PlanState {
  const used = plan?.agents_used ?? 0;
  const limit = plan?.agent_limit ?? null;
  if (limit === null) return { used, limit, remaining: null, percent: 0, full: false };
  const remaining = Math.max(limit - used, 0);
  const percent = limit > 0 ? Math.min(Math.round((used / limit) * 100), 100) : 100;
  return { used, limit, remaining, percent, full: remaining === 0 };
}

/** Industry keys a workspace industry matches (stored value + canonical key, e.g. school → education). */
function industryKeys(industry: string | null | undefined): string[] {
  const canonical = canonicalIndustry(industry);
  return [industry, canonical].filter((key): key is string => Boolean(key));
}

/**
 * Templates for the create dialog: those offered for the workspace's industry (template data
 * `industries`), its primary template first, blank last. `showAll` reveals other industries.
 */
export function orderTemplates(templates: readonly IndustryTemplate[], industry: string | null | undefined, showAll: boolean): IndustryTemplate[] {
  const keys = industryKeys(industry);
  const offered = (t: IndustryTemplate) => keys.some((key) => t.industries.includes(key) || t.industry === key);
  const isPrimary = (t: IndustryTemplate) => (keys.length ? keys.some((key) => t.recommended_for.includes(key)) : t.is_recommended);
  const blank = templates.filter((t) => t.key === BLANK_TEMPLATE);
  const rest = templates.filter((t) => t.key !== BLANK_TEMPLATE);
  const own = rest.filter(offered);
  const primary = own.length > 0 ? own : rest.filter((t) => t.is_recommended);
  const others = rest.filter((t) => !primary.includes(t));
  const rank = (t: IndustryTemplate) => (isPrimary(t) ? 0 : 1);
  const sorted = [...primary].sort((a, b) => rank(a) - rank(b));
  return [...sorted, ...(showAll ? others : []), ...blank];
}

export function hasMoreTemplates(templates: readonly IndustryTemplate[], industry: string | null | undefined): boolean {
  return orderTemplates(templates, industry, true).length > orderTemplates(templates, industry, false).length;
}

/** Default template: the industry's primary one shown, else the first recommended, else the first. */
export function defaultTemplate(ordered: readonly IndustryTemplate[], industry?: string | null): string {
  const keys = industryKeys(industry);
  const primary = ordered.find((t) => keys.some((key) => t.recommended_for.includes(key)));
  return (primary ?? ordered.find((t) => t.is_recommended) ?? ordered[0])?.key ?? BLANK_TEMPLATE;
}

/** "Who can use it" option → agent audiences (the chosen audience; public stays public). */
export function audiencesFor(code: number): number[] {
  return [code];
}
